import React from 'react';
import styled from 'styled-components';
import {
  CpHeading03Primary,
  CpBody03Placeholder,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { useIsResponsive, useMediaQuery } from 'hooks/use-media-query';
import { CpSectionWrapper } from '../../common';
import { CpResourcesVariantWidgetProps } from '../types';
import {
  ResourceDescription as ResourceDescriptionAtom,
  ResourceImage,
  ResourceName,
} from './cp-resources-grid';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-03);
  margin-bottom: var(--cp-spacing-06);

  @media (max-width: 480px) {
    gap: var(--cp-spacing-03);
  }
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Subtitle = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

const ResourceSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: var(--cp-spacing-06);
  width: 100%;
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  overflow-y: hidden;
  overflow-x: hidden;

  @media (min-width: 1024px) {
    max-height: 460px;
    /* hide scrollbar */
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &::-webkit-scrollbar-thumb {
      display: none;
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MaximizedResourceCard = styled.div`
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
`;

const MinimizedResourceCard = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  padding: var(--cp-spacing-06);
  padding-left: 0;
  padding-top: var(--cp-spacing-06);
  border-bottom: solid 1px var(--cp-border-subtle-auto-adjust);
  align-items: center;
  justify-content: flex-start;

  ${ResourceList} a:first-child & {
    padding-top: 0;
  }

  img {
    width: 136px;
    height: 120px;
  }
`;

const ResourceSectionList = styled.div`
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--cp-spacing-06);
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    gap: var(--cp-spacing-06);
    margin-top: var(----cp-spacing-06);
  }
`;

const ResourceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ResourceContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-02);
`;

const ResourceDescription = styled(ResourceDescriptionAtom)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  -webkit-box-orient: vertical;
`;

export const CpResourcesList: React.FC<CpResourcesVariantWidgetProps> = ({
  data,
  widget,
}) => {
  const { title, description } = data;
  const background = useGetBackground(widget);
  const isTablet = useMediaQuery('(max-width: 768px)');
  const isResponsive = useIsResponsive();

  return (
    <CpSectionWrapper
      background={background}
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
    >
      <TitleWrapper>
        <Title autoAdjustColor>{title}</Title>
        <Subtitle autoAdjustColor>{description}</Subtitle>
      </TitleWrapper>
      {isResponsive || isTablet ? (
        <ResourceSectionList>
          {data?.resources?.map((i) => (
            <a href={i.ctaLink}>
              <ResourceCard>
                <ResourceImage
                  src={i?.image?.attachment?.location}
                  alt={i.name}
                />
                <ResourceContentWrapper>
                  <ResourceName autoAdjustColor>{i.title}</ResourceName>
                  <ResourceDescription autoAdjustColor>
                    {i.description}
                  </ResourceDescription>
                </ResourceContentWrapper>
              </ResourceCard>
            </a>
          ))}
        </ResourceSectionList>
      ) : (
        <ResourceSectionWrapper>
          <MaximizedResourceCard
            onClick={() => window.open(data?.resources[0]?.ctaLink, '__blank')}
          >
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img
              src={data?.resources?.[0].image?.attachment?.location}
              alt={data?.resources?.[0].image?.attachment?.name}
            />
            <ResourceContentWrapper>
              <ResourceName autoAdjustColor>
                {data?.resources?.[0]?.title}
              </ResourceName>
              <ResourceDescription autoAdjustColor>
                {data?.resources?.[0]?.description}
              </ResourceDescription>
            </ResourceContentWrapper>
          </MaximizedResourceCard>

          <ResourceList>
            {data?.resources?.slice(1, data?.resources?.length).map((i) => (
              <a href={i.ctaLink} target="_blank" rel="noreferrer">
                <MinimizedResourceCard className="minimizedResourceCard">
                  {i?.image?.attachment?.location && (
                    <ResourceImage
                      src={i?.image?.attachment?.location}
                      alt={i?.name}
                    />
                  )}
                  <ResourceContentWrapper>
                    <ResourceName autoAdjustColor>{i?.title}</ResourceName>
                    <ResourceDescription autoAdjustColor>
                      {i?.description}
                    </ResourceDescription>
                  </ResourceContentWrapper>
                </MinimizedResourceCard>
              </a>
            ))}
          </ResourceList>
        </ResourceSectionWrapper>
      )}
    </CpSectionWrapper>
  );
};
